import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Alert,
  Grid,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { fetchCurrentUser, updateOrganizationTimezone } from '../userSlice';
import TimezoneSelect from 'react-timezone-select';
import OnboardingLayout from './OnboardingLayout';
import onboardingService from '../services/onboardingService';
import { getOrganizationDetails } from '../services/organizationService';
import CountrySelect from './CountrySelect';

// Time options for working hours
const TIME_OPTIONS = Array.from({ length: 24 }, (_, i) => {
  const hour = i.toString().padStart(2, '0');
  return {
    value: `${hour}:00`,
    label: `${hour}:00`,
  };
});

export default function OnboardingWelcomeStep() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  
  // Get user's current timezone for auto-detection
  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  
  const [formData, setFormData] = useState({
    name: '',
    timeZone: userTimezone,
    defaultStartTime: '08:00',
    defaultEndTime: '18:00',
    companyAddress: {
      line1: '',
      line2: '',
      city: '',
      state: '',
      postal_code: '',
      country: ''
    }
  });
  
  const [onboardingStatus, setOnboardingStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [initialLoading, setInitialLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [onboardingData, orgData] = await Promise.all([
          onboardingService.getOnboardingStatus(),
          getOrganizationDetails()
        ]);
        
        setOnboardingStatus(onboardingData);
        
        // Pre-populate form with existing organization data, but keep auto-detected timezone if no org timezone exists
        if (orgData) {
          setFormData({
            name: orgData.name || '',
            // Only use org timezone if it exists AND is not the default UTC
            timeZone: (orgData.timeZone && orgData.timeZone !== 'UTC') ? orgData.timeZone : userTimezone,
            defaultStartTime: orgData.defaultStartTime || '08:00',
            defaultEndTime: orgData.defaultEndTime || '18:00',
            companyAddress: orgData.companyAddress || {
              line1: '',
              line2: '',
              city: '',
              state: '',
              postal_code: '',
              country: ''
            }
          });
        }
      } catch (error) {
        console.error('Error fetching initial data:', error);
        setError('Failed to load organization data');
      } finally {
        setInitialLoading(false);
      }
    };

    fetchData();
  }, [userTimezone]); // Add userTimezone as dependency

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Handle nested address fields
    if (name.startsWith('companyAddress.')) {
      const addressField = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        companyAddress: {
          ...prev.companyAddress,
          [addressField]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleTimezoneChange = (tz) => {
    setFormData(prev => ({
      ...prev,
      timeZone: tz.value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Validate form
      if (!formData.name.trim()) {
        throw new Error('Organization name is required');
      }

      if (formData.defaultStartTime >= formData.defaultEndTime) {
        throw new Error('End time must be after start time');
      }

      // Update organization settings
      await onboardingService.updateOrganizationSettings(formData);
      
      // Mark welcome step as completed
      await onboardingService.updateOnboardingStep('welcome', 'complete');
      
      // Update the Redux store with the new timezone
      dispatch(updateOrganizationTimezone(formData.timeZone));
      
      // Refresh user data to get updated onboarding status
      await dispatch(fetchCurrentUser());
      
      // Navigate to next step
      navigate('/onboarding/billing');
    } catch (error) {
      console.error('Error updating organization:', error);
      setError(error.response?.data?.error || error.message || 'Failed to update organization settings');
    } finally {
      setLoading(false);
    }
  };

  if (initialLoading) {
    return (
      <OnboardingLayout
        currentStep="welcome"
        completedSteps={[]}
        title="Loading..."
      >
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography>Loading organization data...</Typography>
        </Box>
      </OnboardingLayout>
    );
  }

  return (
    <OnboardingLayout
      currentStep="welcome"
      completedSteps={onboardingStatus?.completedSteps || []}
      skippedSteps={onboardingStatus?.skippedSteps || []}
      title="Welcome! Let's set up your organization"
      subtitle="First, let's configure your organization's basic settings and working hours."
    >
      <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Grid container spacing={3}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Organization Name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              required
              placeholder="Enter your organization name"
              helperText="This is how your organization will be displayed to team members"
            />
          </Grid>

          {/* Company Address Section */}
          <Grid item xs={12}>
            <Typography variant="h6" sx={{ mt: 2, mb: 2 }}>
              Company Address
            </Typography>
          </Grid>
          
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Address Line 1"
              name="companyAddress.line1"
              value={formData.companyAddress.line1}
              onChange={handleInputChange}
              placeholder="Street address"
            />
          </Grid>
          
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Address Line 2 (Optional)"
              name="companyAddress.line2"
              value={formData.companyAddress.line2}
              onChange={handleInputChange}
              placeholder="Apartment, suite, etc."
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="City"
              name="companyAddress.city"
              value={formData.companyAddress.city}
              onChange={handleInputChange}
              placeholder="City"
            />
          </Grid>
          
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="State/Province"
              name="companyAddress.state"
              value={formData.companyAddress.state}
              onChange={handleInputChange}
              placeholder="State"
            />
          </Grid>
          
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="Postal Code"
              name="companyAddress.postal_code"
              value={formData.companyAddress.postal_code}
              onChange={handleInputChange}
              placeholder="ZIP/Postal code"
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <CountrySelect
              value={formData.companyAddress.country}
              onChange={(value) => handleInputChange({ target: { name: 'companyAddress.country', value } })}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="body1" sx={{ mb: 1 }}>
              Time Zone
            </Typography>
            <Box sx={{ mb: 1 }}>
              {formData.timeZone ? (
                <TimezoneSelect
                  key={formData.timeZone}
                  value={formData.timeZone}
                  onChange={handleTimezoneChange}
                />
              ) : (
                <Typography>Loading timezone...</Typography>
              )}
            </Box>
          </Grid>

          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Default Start Time</InputLabel>
              <Select
                name="defaultStartTime"
                value={formData.defaultStartTime}
                onChange={handleInputChange}
                label="Default Start Time"
              >
                {TIME_OPTIONS.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Default End Time</InputLabel>
              <Select
                name="defaultEndTime"
                value={formData.defaultEndTime}
                onChange={handleInputChange}
                label="Default End Time"
              >
                {TIME_OPTIONS.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <Typography variant="body2" color="text.secondary">
              <strong>Working Hours:</strong> These will be the default operating hours for your meeting rooms. 
              You can customize individual room hours later.
            </Typography>
          </Grid>
        </Grid>

        <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            type="submit"
            variant="contained"
            size="large"
            disabled={loading}
            sx={{ minWidth: 120 }}
          >
            {loading ? 'Saving...' : 'Continue'}
          </Button>
        </Box>
      </Box>
    </OnboardingLayout>
  );
} 